import type { CodeableConcept } from "@medplum/fhirtypes";
import { Command, CommandGroup, CommandItem, CommandList } from "@repo/ui/components/ui/command";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";
import { type RxDrugOption, useRxNormSearch } from "@/shared/hooks/use-rxnorm-search";
import { SearchPicker } from "@/shared/pickers/search-picker";

interface MedicationPickerProps {
  value?: CodeableConcept;
  onChange: (value: CodeableConcept | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
}

/** Only strengths carry an RxCUI; free text covers brands and compounds absent from RxTerms. */
export function MedicationPicker({
  value,
  onChange,
  placeholder = "Search medications...",
  disabled,
}: MedicationPickerProps) {
  const [drug, setDrug] = useState<RxDrugOption | null>(null);
  return (
    <SearchPicker.Root placeholder={placeholder} onClose={() => setDrug(null)}>
      <SearchPicker.Trigger
        label={value?.coding?.[0]?.display ?? value?.text}
        onClear={value ? () => onChange(undefined) : undefined}
        clearLabel="Clear medication"
        disabled={disabled}
      />
      <SearchPicker.Content>
        <MedicationResults value={value} onChange={onChange} drug={drug} setDrug={setDrug} />
      </SearchPicker.Content>
    </SearchPicker.Root>
  );
}

function MedicationResults({
  value,
  onChange,
  drug,
  setDrug,
}: MedicationPickerProps & {
  drug: RxDrugOption | null;
  setDrug: (drug: RxDrugOption | null) => void;
}) {
  const { state, actions } = SearchPicker.useContext();
  const { options, isLoading } = useRxNormSearch(state.query);
  const typed = state.query.trim();

  // Distinct keys discard cmdk's input and selection state when moving between views.
  if (drug) {
    return (
      <Command key="strengths" shouldFilter={false}>
        <CommandList>
          <CommandGroup heading={drug.name}>
            <CommandItem value="__back" onSelect={() => setDrug(null)}>
              <ChevronLeft className="size-4 opacity-50" />
              <span className="text-xs text-muted-foreground">Back to results</span>
            </CommandItem>
            {drug.strengths.map((strength) => (
              <CommandItem
                key={strength.rxcui}
                value={strength.rxcui}
                onSelect={() => {
                  onChange({ coding: [strength.coding], text: strength.coding.display });
                  actions.close();
                }}
                data-checked={value?.coding?.[0]?.code === strength.rxcui ? "true" : undefined}
              >
                <span className="text-sm">{strength.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    );
  }

  return (
    <SearchPicker.Search key="search">
      <SearchPicker.Results isLoading={isLoading} count={options.length} emptyMessage="No medications found.">
        <CommandGroup>
          {options.map((option) => (
            <CommandItem key={option.name} value={option.name} onSelect={() => setDrug(option)}>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{option.name}</span>
                <span className="text-xs text-muted-foreground">
                  {option.strengths.length} {option.strengths.length === 1 ? "strength" : "strengths"}
                </span>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </SearchPicker.Results>
      {typed.length >= 2 && (
        <SearchPicker.Footer>
          <CommandItem
            value="__free-text"
            onSelect={() => {
              onChange({ text: typed });
              actions.close();
            }}
          >
            <span className="text-sm">
              Use “<span className="font-medium">{typed}</span>” as typed
            </span>
          </CommandItem>
        </SearchPicker.Footer>
      )}
    </SearchPicker.Search>
  );
}
